import _ from 'lodash';
import React, { useState, useRef, useEffect } from 'react';
import {
  GridBag,
  BodyText,
  Button,
  Div,
  Gutter,
  Link,
  Headline,
  Dropdown,
  Divider,
  Loader,
  List,
  Icon,
  Flex,
  Accordion,
} from '@gaiads/telia-react-component-library';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Prompt, useHistory } from 'react-router-dom';

import { mapBackendErrorCodeToLocalizedErrorMsg, resolveAxiosErrorResponse } from '../../../utils/errors';
import { getMainSiteNavigationComponentHeight } from '../../App/Header/Header';
import { setNotification } from '../../../store/ui';
import { productServiceAdminBundlesClient } from '../../../api-client/product-service-client';

import { BundleEditProps, Errors, validate, createBackendBundle, ensureEditableItemInputChangesSavedOnSaveBtnClick } from './utils';

import NameSection from './Name/Name';
import ProductVariantsSection from './ProductVariants/ProductVariants';
import DescriptionSection from './Description/Description';
import ImagesSection from './Images/Images';

import './BundleEdit.scss';

const saveBundleButtonId = 'save-bundle-btn';

export function BundleEdit(props: BundleEditProps) {
  const {
    id,
    name,
    handleNameChange,
    nameTranslations,
    handleNameTranslationsChange,
    financeId,
    handleFinanceIdChange,
    description,
    handleDescriptionChange,
    descriptionTranslations,
    handleDescriptionTranslationsChange,
    translationsMetadata,
    images,
    handleImagesChange,
    mainItemOptionIds,
    handleMainItemOptionIdsChange,
    items,
    handleItemsChange,
    variants,
    handleVariantsChange,
    allCategoriesInSystem,
    allSalesChannelsInSystem,
    allCountriesInSystem,
    bundlePrices,
    currentLocale,
    locales,
    handleLocaleChange,
    addMode,
  } = props;

  const { t } = useTranslation('bundlescreen');
  const history = useHistory();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Errors>();

  // For checking if user has made changes and tries to navigate away
  const bundleWithoutAnyChangesRef = useRef(
    createBackendBundle({
      id,
      name,
      nameTranslations,
      financeId,
      description,
      descriptionTranslations,
      translationsMetadata,
      images,
      mainItemOptionIds,
      items,
    }),
  );

  const [saveBundleChanges, setSaveBundleChanges] = useState<boolean>(false);

  const handleSubmit = async (): Promise<boolean> => {
    const { valid, errors } = validate({ name, financeId, description, descriptionTranslations, mainItemOptionIds, items, locales, t });

    if (!valid) {
      setErrors(errors);
      dispatch(
        setNotification({
          text: t('editscreen.errors.validation'),
          show: true,
          type: 'warning',
        }),
      );
    } else {
      setErrors(undefined);
      setLoading(true);
      const bundle = createBackendBundle({
        id,
        name,
        nameTranslations,
        financeId,
        description,
        descriptionTranslations,
        translationsMetadata,
        images,
        mainItemOptionIds,
        items,
      });
      try {
        if (addMode) {
          await productServiceAdminBundlesClient.postAdminBundle(bundle);
        } else if (id) {
          await productServiceAdminBundlesClient.putAdminBundle(id, bundle);
        }
        history.push('/bundles');
        return true;
      } catch (error) {
        setLoading(false);
        const defaultErrorMsg = addMode ? t('editscreen.errors.create_bundle') : t('editscreen.errors.update_bundle');
        dispatch(
          setNotification({
            text: mapBackendErrorCodeToLocalizedErrorMsg(resolveAxiosErrorResponse(error)?.data, defaultErrorMsg),
            show: true,
            type: 'warning',
          }),
        );
      }
    }
    return false;
  };

  useEffect(() => {
    const submit = async () => {
      const success = await handleSubmit();
      if (!success) {
        setSaveBundleChanges(false);
      }
    };
    if (saveBundleChanges) {
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveBundleChanges]);

  const sectionTitles: { [key: string]: { sectionHasErrors: boolean } } = {
    [t('editscreen.section_titles.name')]: { sectionHasErrors: Boolean(errors?.name || errors?.financeId) },
    [t('editscreen.section_titles.product_variants')]: {
      sectionHasErrors: Boolean(errors?.mainProductVariants) || Boolean(errors?.addonProductVariants),
    },
    [t('editscreen.section_titles.description')]: { sectionHasErrors: Boolean(errors?.description) },
    [t('editscreen.section_titles.images')]: { sectionHasErrors: false },
  };

  const [activeSectionTab, setActiveSectionTab] = useState<string>(Object.keys(sectionTitles)[0].replaceAll(' ', ''));

  const scrollToSection = (ref: HTMLDivElement | null) => {
    if (ref) {
      const pos = ref.offsetTop - getMainSiteNavigationComponentHeight();
      window.scrollTo({ top: pos, behavior: 'smooth' });
    }
  };
  const sectionsRef = useRef<(HTMLDivElement | null)[]>(new Array(Object.keys(sectionTitles).length));

  const handleSectionTabClick = (title: string) => {
    setActiveSectionTab(title.replaceAll(' ', ''));
    scrollToSection(sectionsRef.current[Object.keys(sectionTitles).findIndex(sectionTitle => sectionTitle === title)]);
  };

  const renderSectionContent = (title: string) => {
    switch (title) {
      case t('editscreen.section_titles.name'):
        return (
          <NameSection
            title={title}
            name={name}
            financeId={financeId}
            locale={currentLocale}
            nameTranslations={nameTranslations}
            handleNameChange={handleNameChange}
            handleNameTranslationsChange={handleNameTranslationsChange}
            handleFinanceIdChange={handleFinanceIdChange}
            nameError={errors?.name}
            financeIdError={errors?.financeId}
          />
        );
      case t('editscreen.section_titles.product_variants'):
        return (
          <ProductVariantsSection
            mainItemOptionIds={mainItemOptionIds}
            handleMainItemOptionIdsChange={handleMainItemOptionIdsChange}
            items={items}
            handleItemsChange={handleItemsChange}
            variants={variants}
            handleVariantsChange={handleVariantsChange}
            allCategoriesOfSystem={allCategoriesInSystem}
            allSalesChannelsInSystem={allSalesChannelsInSystem}
            allCountriesInSystem={allCountriesInSystem}
            bundlePrices={bundlePrices}
            currentLocale={currentLocale}
            locales={locales}
            mainItemError={errors?.mainProductVariants}
            addonsError={errors?.addonProductVariants}
          />
        );
      case t('editscreen.section_titles.description'):
        return (
          <DescriptionSection
            title={title}
            description={description}
            descriptionTranslations={descriptionTranslations}
            locale={currentLocale}
            handleDescriptionChange={handleDescriptionChange}
            handleDescriptionTranslationsChange={handleDescriptionTranslationsChange}
            errors={errors?.description}
          />
        );
      case t('editscreen.section_titles.images'):
        return <ImagesSection title={title} images={images} handleImagesChange={handleImagesChange} />;
    }
  };

  return (
    <Div>
      <Prompt
        message={() => {
          // Navigation is caused by user clicking the save changes -button
          if (saveBundleChanges) {
            return true;
          }
          const bundleWithPossibleChanges = createBackendBundle({
            id,
            name,
            nameTranslations,
            financeId,
            description,
            descriptionTranslations,
            translationsMetadata,
            images,
            mainItemOptionIds,
            items,
          });
          const unsavedChanges = !_.isEqual(bundleWithPossibleChanges, bundleWithoutAnyChangesRef.current);
          return unsavedChanges ? t('editscreen.confirm_unsaved_changes') : true;
        }}
      />
      {loading && (
        <Div className="bundle-edit-loading-overlay" alignTextToCenter padding={{ top: 'xlg' }}>
          <Loader size="lg" />
        </Div>
      )}
      <GridBag>
        {/* Left-side container */}
        <GridBag.Item md={9}>
          <BodyText>{addMode ? t('editscreen.add_bundle_title') : t('editscreen.update_bundle_title')}</BodyText>
          <Headline size="lg">{name}</Headline>
          <Gutter size="xlg" />
          <Div padding={{ size: 'md' }} boxShadow="sm">
            {Object.keys(sectionTitles).map((title, index) => (
              <Div key={title}>
                <div id={title} ref={el => (sectionsRef.current[index] = el)} /*for navigating to section from a section title*/></div>
                {renderSectionContent(title)}
                <Div>
                  <Gutter size="lg" />
                  <Divider width={90} />
                  <Gutter size="lg" />
                </Div>
              </Div>
            ))}
          </Div>
        </GridBag.Item>
        {/* Right-side container */}
        <GridBag.Item md={3}>
          <Div>
            <Div alignTextToRight padding={{ top: 'sm' }}>
              <Div inlineBlock={true} padding={{ left: 'md', top: 'sm' }}>
                <Button
                  id={saveBundleButtonId}
                  onClick={async () => {
                    await ensureEditableItemInputChangesSavedOnSaveBtnClick(saveBundleButtonId);
                    setSaveBundleChanges(true);
                  }}
                >
                  {t('editscreen.buttons.save_changes')}
                </Button>
              </Div>
            </Div>
            <Gutter size="xlg" />
            <Div>
              <Accordion heading={t('editscreen.translation')} initiallyExpanded={true} showOverflow={true}>
                <Dropdown
                  name="currentLocale"
                  label={t('editscreen.translation')}
                  onChange={(_, option) => {
                    handleLocaleChange(Number(option.value));
                  }}
                  selectedOption={{ label: currentLocale.name, value: currentLocale.id.toString() }}
                  options={locales.map(locale => ({ label: locale.name, value: locale.id.toString() }))}
                />
                <Gutter />
                <Dropdown
                  label={t('editscreen.reference')}
                  onChange={() => undefined}
                  selectedOption={{ label: t('editscreen.reference_english'), value: '' }}
                  options={[]}
                  disabled={true}
                />
              </Accordion>
              <Gutter />
              <Accordion heading={t('editscreen.sections')} initiallyExpanded={true}>
                <List numbered={false} disc={false}>
                  {Object.entries(sectionTitles).map(([title, metaData]) => (
                    <List.Item key={title}>
                      <Flex>
                        <Link
                          to={`#${title.replaceAll(' ', '')}`}
                          className={title.replaceAll(' ', '') === activeSectionTab && 'bundle_form_sections_nav_list__active-tab'}
                        >
                          <BodyText
                            color="black"
                            fontWeight={title.replaceAll(' ', '') === activeSectionTab ? 'bold' : 'normal'}
                            hoverColor="purple500"
                            clickableAppearance
                            onClick={() => handleSectionTabClick(title)}
                          >
                            {title}
                          </BodyText>
                        </Link>
                        {metaData.sectionHasErrors && (
                          <Icon
                            data-testid={`section-${title}-has-errors-indicator`}
                            name="alert-filled"
                            size="md"
                            color="red400"
                            margin={{ left: 'sm' }}
                            title={t('editscreen.section_has_errors_tooltip')}
                          />
                        )}
                      </Flex>
                    </List.Item>
                  ))}
                </List>
              </Accordion>
            </Div>
          </Div>
        </GridBag.Item>
      </GridBag>
    </Div>
  );
}
